import React, { useEffect, useState } from "react";
import { type GetStaticPaths, type GetStaticProps, type NextPage } from "next";
import { useRouter } from "next/router";
import { groq } from "next-sanity";
import { MDXRemote, type MDXRemoteSerializeResult } from "next-mdx-remote";
import * as Sentry from "@sentry/nextjs";
import {
  getClient,
  usePreviewSubscription,
  getAnnouncementBanner,
  type Banner,
  type CaseStudy,
  getCaseStudy,
  customerFragment,
  getAllEmbeddedWhitepapers,
  type EmbeddedWhitepaper,
} from "lib/sanity";
import { trackEvent } from "lib/analytics";
import { Box, Text, Flex } from "@chakra-ui/react";
import { components } from "components/mdx/components";
import { Page } from "components/page";
import { Article, ArticleContainer } from "components/article";
import { ArticleSidebar, type SidebarHeading } from "components/sidebar";
import { useHeaderHeight } from "contexts/header-height";
import NotFound from "pages/404";
import { getArticleHeadings, serializeArticle } from "components/mdx/utils";
import { getPageMetaImage } from "utils";
import { Section, sectionSpacingY } from "components/section";
import { Breadcrumbs } from "ui/breadcrumbs";
import { FillImage } from "components/images";

type PageProps = {
  caseStudy: CaseStudy;
  slug: string;
  preview?: boolean;
  mdx: MDXRemoteSerializeResult;
  headings: SidebarHeading[];
  announcementBanner?: Banner;
  allEmbeddedWhitepapers: EmbeddedWhitepaper[];
};

const caseStudyQuery = groq`
  *[_type == "caseStudy" && customer->slug == $slug][0] {
    title,
    content,
    image,
    customer->{${customerFragment}}
  }
`;

const CaseStudyPage: NextPage<Readonly<PageProps>> = ({
  caseStudy,
  slug,
  preview,
  mdx,
  headings,
  announcementBanner,
  allEmbeddedWhitepapers,
}) => {
  const router = useRouter();
  const [mdxSource, setMdxSource] = useState<MDXRemoteSerializeResult>(mdx);
  const { headerHeight } = useHeaderHeight();

  const { data: previewCaseStudy } = usePreviewSubscription(caseStudyQuery, {
    params: { slug },
    initialData: caseStudy,
    enabled: Boolean(preview && slug),
  });

  const serializeMdx = async (content: string) => {
    const newMdx = await serializeArticle(content);
    setMdxSource(newMdx);
  };

  useEffect(() => {
    if (caseStudy && slug) {
      trackEvent("Case Study Viewed", {
        post_name: caseStudy.title,
        post_customer: caseStudy.customer.name,
        post_slug: slug,
      });
    }
  }, [caseStudy, slug]);

  useEffect(() => {
    if (previewCaseStudy) {
      serializeMdx(previewCaseStudy.content).catch((error) => {
        Sentry.captureException(error);
      });
    }
  }, [previewCaseStudy]);

  if (!router.isFallback && (!slug || !caseStudy)) {
    return <NotFound />;
  }

  if (!caseStudy || !mdxSource) {
    return null;
  }

  const pageTitle = caseStudy.metaTitle ?? `Hightouch + ${caseStudy.customer.name}`;
  const pageDescription = caseStudy.metaDescription ?? caseStudy.title;

  return (
    <Page
      hasSmoothScroll
      title={pageTitle}
      description={pageDescription}
      image={getPageMetaImage({ src: caseStudy.metaImageUrl, alt: caseStudy.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <Section as="article" my={0} pb={sectionSpacingY}>
        <Breadcrumbs
          fontFamily="inter"
          crumbs={[{ title: "Customers", href: "/customers" }]}
          mt={{ base: "tinier", md: "smaller" }}
        />

        <Flex
          align="center"
          my={{ base: "small", md: "bigger" }}
          w={{ base: "190px", md: "240px" }}
          h={{ base: "40px", md: "44px" }}
          pos="relative"
        >
          <FillImage
            priority
            placeholder="empty"
            src={caseStudy.customer.logoUrl + "?w=800"}
            alt={caseStudy.customer.name + "."}
            style={{
              objectFit: "contain",
              objectPosition: "left",
            }}
          />
        </Flex>
        <Box maxW="4xl">
          <Text as="h1" textStyle={{ base: "article.h3", md: "article.h2" }} mt={0}>
            {caseStudy.title}
          </Text>

          <Text mt={8} textStyle="article.p" color="brand.textSecondary.light">
            {caseStudy.customer.about}
          </Text>
        </Box>

        <ArticleContainer mt={20} pt={20} borderTop="1px" borderTopColor="brand.border.light">
          <Article>
            <MDXRemote lazy components={components({ type: "customer", allEmbeddedWhitepapers })} {...mdxSource} />
          </Article>

          <ArticleSidebar headings={headings} path={`customers/${slug}`} />
        </ArticleContainer>
      </Section>
    </Page>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params, preview = false }) => {
  const slug = params?.slug ? String(params?.slug) : "";
  const [caseStudy, announcementBanner, allEmbeddedWhitepapers] = await Promise.all([
    getCaseStudy(slug),
    getAnnouncementBanner(),
    getAllEmbeddedWhitepapers(),
  ]);

  if (!caseStudy) {
    return {
      notFound: true,
    };
  }

  const mdx = await serializeArticle(caseStudy.content);

  return {
    props: {
      preview,
      slug,
      caseStudy,
      mdx,
      headings: getArticleHeadings(caseStudy.content),
      announcementBanner,
      allEmbeddedWhitepapers,
    },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const caseStudySlugs: CaseStudy[] = await getClient(false).fetch(
    groq`*[_type == "caseStudy"] { customer->{ slug } }`,
  );
  const slugs = caseStudySlugs?.map((s) => s.customer.slug);

  return {
    paths: slugs.map((slug: string) => ({ params: { slug } })),
    fallback: "blocking",
  };
};

export default CaseStudyPage;
